import { AhuState, Entity } from '@app/shared/models/common-models'
import type {
  Ahu,
  AhuResults,
  Category,
  Message,
  SystemDrawing,
  ProductResultEntity,
  AirFlowRangeDiagramData,
  CapacityDiagramData,
} from '@app/ahu/state/ahu.model'

export interface CommonAhuDto {
  UnitURL: string
  EuroventImageUrl: string
  Messages: Message[]
}

export interface CommonDesignAhuDto extends CommonAhuDto {
  UnitImageURL: string
  UnitConnectionURL: string
  EcodesignLabelImageURL: string
  IsMinMaxEnabled: boolean
  Category: string
  Errors: boolean
  IsHeatPumpAhu: boolean
  AirFlowRangeDiagramData: AirFlowRangeDiagramData
  CapacityDiagramData: CapacityDiagramData[]
  TranslationOverrides: Record<string, string>
}

export interface AhuDto extends CommonDesignAhuDto {
  UnitCode: string
  Ahu: Entity
  Categories: Category[]
}

export interface AhuUpdateDto extends CommonDesignAhuDto {
  Entity: Entity
  Title: string // This is UnitCode... Don't ask...
}

export interface AhuResultDto extends CommonAhuDto {
  UnitCode: string
  State: AhuState
  Ahu: ProductResultEntity
  SystemDrawingDescriptions: SystemDrawing[]
}

export interface QuickCalcAhuDto extends AhuDto {
  Language: string
}

/**
 * Map the common properties in dtos to the domain ahu model
 * @param dto Common dto
 * @param projectId project id (optional)
 */
const convertCommonAhuDtoToDomainModel = (
  dto: CommonDesignAhuDto,
  projectId: string,
): Omit<Ahu, 'Id' | 'UnitCode' | 'Entity' | 'Categories'> => {
  return {
    ProjectId: projectId,
    IsMinMaxEnabled: dto.IsMinMaxEnabled,
    UnitURL: dto.UnitURL,
    UnitImageURL: dto.UnitImageURL,
    EcodesignLabelImageURL: dto.EcodesignLabelImageURL,
    EuroventImageUrl: dto.EuroventImageUrl,
    UnitConnectionURL: dto.UnitConnectionURL,
    Errors: dto.Errors,
    Category: dto.Category,
    Messages: dto.Messages,
    IsHeatPumpAhu: dto.IsHeatPumpAhu,
    AirFlowRangeDiagramData: dto.AirFlowRangeDiagramData,
    CapacityDiagramData: dto.CapacityDiagramData,
    TranslationOverrides: dto.TranslationOverrides,
    ShouldRefresh: false,
  }
}

export function ConvertAhuDtoToDomainModel(dto: AhuDto, projectId: string): Ahu {
  return {
    ...convertCommonAhuDtoToDomainModel(dto, projectId),
    Id: dto.Ahu.Id,
    UnitCode: dto.UnitCode,
    Entity: dto.Ahu,
    Category: dto.Category,
    Categories: dto.Categories,
    CapacitySupplyImage: null,
  } as Ahu
}

export function ConvertAhuUpdateDtoToDomainModel(dto: AhuUpdateDto): Ahu {
  return {
    ...convertCommonAhuDtoToDomainModel(dto, null),
    Id: dto.Entity.Id,
    UnitCode: dto.Title,
    Entity: dto.Entity,
    Category: dto.Category,
  } as Ahu
}

export function ConvertAhuResultDtoToDomainModel(dto: AhuResultDto): AhuResults {
  return {
    State: dto.State,
    Ahu: dto.Ahu,
    SystemDrawingDescriptions: dto.SystemDrawingDescriptions,
    Messages: dto.Messages,
  } as AhuResults
}
